import { API_ENDPOINTS } from '@/config/api-endpoints';
import httpInstance from '@config/http';
import { BaseService } from './base.service';
/**
 * Voice Recordings Service
 * Handles all voice recording-related API calls
 */
class VoiceRecordingsService extends BaseService {
    /**
     * Fetch all voice recordings
     * @returns Array of voice recordings for all languages
     * @throws ApiError if fetch fails
     */
    async getRecordings() {
        try {
            const response = await httpInstance.get(API_ENDPOINTS.VOICE_RECORDINGS);
            return response.data.data;
        }
        catch (error) {
            throw this.handleError(error, 'Failed to fetch recordings');
        }
    }
    /**
     * Get audio blob for a specific language and type
     * @param languageKey - Language key (e.g., 'kannada', 'english')
     * @param type - Type of audio ('consentAudio' or 'referenceAudio')
     * @returns Audio blob
     * @throws ApiError if fetch fails
     */
    async getRecordingAudio(languageKey, type) {
        try {
            if (!languageKey || languageKey.trim() === '') {
                throw new Error('Language key is required');
            }
            if (!type || !['consentAudio', 'referenceAudio'].includes(type)) {
                throw new Error('Valid audio type is required');
            }
            const response = await httpInstance.get(API_ENDPOINTS.VOICE_RECORDINGS_AUDIO, {
                params: {
                    languageKey,
                    type,
                },
                responseType: 'blob',
            });
            return response.data;
        }
        catch (error) {
            throw this.handleError(error, 'Failed to fetch recording audio');
        }
    }
    /**
     * Upload voice recordings for a specific language
     * @param request - Upload request containing language key and audio blobs
     * @returns Upload response
     * @throws ApiError if upload fails
     */
    async uploadRecordings(request) {
        try {
            this.validateUploadRequest(request);
            const formData = this.createUploadFormData(request);
            const response = await httpInstance.post(API_ENDPOINTS.VOICE_RECORDINGS, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            return response.data;
        }
        catch (error) {
            throw this.handleError(error, 'Failed to upload recordings');
        }
    }
    /**
     * Delete all recordings for a specific language (soft delete)
     * @param languageKey - Language key to delete recordings for
     * @returns Delete response
     * @throws ApiError if deletion fails
     */
    async deleteRecordings(languageKey) {
        try {
            if (!languageKey || languageKey.trim() === '') {
                throw new Error('Language key is required');
            }
            const response = await httpInstance.delete(`${API_ENDPOINTS.VOICE_RECORDINGS}/${languageKey}`);
            return response.data;
        }
        catch (error) {
            throw this.handleError(error, 'Failed to delete recordings');
        }
    }
    /**
     * Validate upload request
     */
    validateUploadRequest(request) {
        if (!request.languageKey || request.languageKey.trim() === '') {
            throw new Error('Language key is required');
        }
        if (!request.consentAudio) {
            throw new Error('Consent audio is required');
        }
        if (!request.referenceAudio) {
            throw new Error('Sample voice audio is required');
        }
        if (!request.consentText || request.consentText.trim() === '') {
            throw new Error('Consent text is required');
        }
    }
    /**
     * Create FormData object for upload request
     */
    createUploadFormData(request) {
        const formData = new FormData();
        formData.append('languageKey', request.languageKey);
        formData.append('consentAudio', request.consentAudio);
        formData.append('referenceAudio', request.referenceAudio);
        formData.append('consentText', request.consentText);
        return formData;
    }
}
// Export singleton instance
export const voiceRecordingsService = new VoiceRecordingsService();
